import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../services/apiClient';

// ── Lines from Fieldwire (Ryan, Oct 22 2026) ────────────────────────────────────
// The end of tasking a bid: each Fieldwire CATEGORY on the bid's project becomes one bid
// line (Ryan: "always one line per category" — the first cut grouped by task name and
// every trench run came in as its own line). Quantity = how many tasks, EACH — or, when
// the task names carry their lengths ("Trench A–B 45 LF", "120'"), the feet added up, LF.
// Tasks with no category still group by name after their ID ("01b · No. 7 Pull Box").
// The lines are ordinary bid lines. Run it again to sync: a line made from a category
// (fw_kind) gets the new quantity and keeps everything typed on it; new categories come
// in as new lines; one with no tasks left goes to qty 0, flagged red. ↺ Start over takes
// every line it made out and reads the tasks fresh.

// "01b · No. 7 Pull Box, Mod." → the part after the ID; a name with no ID is its own kind.
// An ID is short, has a digit and no spaces (01a, SP1, 0201, #12).
export function taskKind(name) {
  const parts = String(name || '').split(' · ').map(s => s.trim()).filter(Boolean);
  if (!parts.length) return null;
  const hasId = parts.length > 1 && /^[#A-Za-z0-9.-]{1,8}$/.test(parts[0]) && /\d/.test(parts[0]);
  const label = (hasId ? parts.slice(1) : parts).join(' · ');
  return { key: label.toLowerCase().replace(/\s+/g, ' '), label, id: hasId ? parts[0] : null };
}
// Feet written in a task name: "45 LF", "45LF", "120'", "35 ft", "1,200 feet".
export function feetIn(name) {
  const m = String(name || '').match(/(\d[\d,]*(?:\.\d+)?)\s*(?:l\.?f\.?|ft\.?|feet|foot|'|’)(?![a-z0-9])/i);
  return m ? Number(m[1].replace(/,/g, '')) : null;
}
// A length in a clipped table row's columns: "LEN. UNIT: 150.2'; …" → 150.2 (Ryan, Oct 22
// 2026: the trench runs came from the conduit run table, the lengths are in its column).
export function lengthIn(details) {
  for (const part of String(details || '').split(/;\s*/)) {
    const m = part.match(/^([^:]+):\s*(.+)$/); if (!m) continue;
    if (!/\b(len|length|lf|feet|footage|ft)\b|len\./i.test(m[1])) continue;
    const n = m[2].match(/(\d[\d,]*(?:\.\d+)?)/); if (n) return Number(n[1].replace(/,/g, ''));
  }
  return null;
}
// The groups with what each line would say: a Fieldwire category; else (no category) the
// clipped table a Help-task task came from; else the name after its ID.
export function groupTasks(tasks, rows = {}) {
  const groups = new Map();
  for (const t of tasks) {
    const k = taskKind(t.name); if (!k) continue;
    const team = String(t.team_name || '').trim();
    const row = rows[t.id] || null;
    const key = t.team_id && team ? 'cat:' + t.team_id : row ? 'clip:' + row.clip_id : 'kind:' + k.key;
    const g = groups.get(key) || { key, label: t.team_id && team ? team : row ? (row.clip_name || 'Clipped table') : k.label, count: 0, ids: [], feet: [], fromTable: false };
    g.count++;
    const id = k.id || (row && row.id) || null; if (id) g.ids.push(id);
    let f = feetIn(k.label);
    if (f == null && row) { f = lengthIn(row.details); if (f != null) g.fromTable = true; }
    if (f != null) g.feet.push(f);
    groups.set(key, g);
  }
  return [...groups.values()].map(g => {
    // Lengths only when most tasks have one and they differ — a "30' Steel Post" on every
    // pole is a pole size, not footage.
    const useFeet = g.feet.length > 0 && g.feet.length >= g.count / 2 && (new Set(g.feet).size > 1 || (g.count === 1 && !g.ids.length));
    const qty = useFeet ? Math.round(g.feet.reduce((a, b) => a + b, 0) * 100) / 100 : g.count;
    return { ...g, qty, unit: useFeet ? 'LF' : 'EACH', noFeet: useFeet ? g.count - g.feet.length : 0 };
  }).sort((a, b) => a.label.localeCompare(b.label));
}
const feetWord = r => (r.fromTable ? 'feet from the table' : 'feet from the task names');
const lineNotes = r => `From Fieldwire — ${r.count} task${r.count === 1 ? '' : 's'}${r.unit === 'LF' ? `, ${feetWord(r)}${r.noFeet ? ` (${r.noFeet} with no length)` : ''}` : ''}${r.ids.length ? ': ' + r.ids.join(', ') : ''}`;

const btn = { padding: '7px 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };

export default function FieldwireLines({ bidId, items, makeItem, onApply, onClose }) {
  const [tasks, setTasks] = useState(null);
  const [err, setErr] = useState('');
  const [off, setOff] = useState(() => new Set());   // groups left unticked
  const [rows, setRows] = useState({});   // task id → the clipped-table row it came from
  useEffect(() => {
    apiFetch(`/projects/${bidId}/fieldwire/hand-task-rows`).then(r => setRows(r && typeof r === 'object' ? r : {})).catch(() => {})
      .finally(() => apiFetch(`/projects/${bidId}/fieldwire/tasks`).then(t => setTasks(Array.isArray(t) ? t : [])).catch(e => setErr(e?.error || 'Could not read the Fieldwire tasks.')));
  }, [bidId]);

  const manual = items.filter(i => i.auto_source !== 'hole_schedule');
  const fwLines = manual.filter(i => i.fw_kind);
  // Lines from the first version (Oct 22, grouped by task name) — Start over replaces them.
  const oldStyle = fwLines.filter(i => !/^(cat|kind):/.test(i.fw_kind)).length;
  const groups = useMemo(() => (tasks ? groupTasks(tasks, rows) : null), [tasks, rows]);
  // Each group and the line it updates (made from it, or one already called the same thing,
  // so a line typed by hand isn't doubled).
  const plan = useMemo(() => {
    if (!groups) return null;
    const keys = new Set(groups.map(g => g.key));
    const byDesc = new Map(manual.filter(i => !i.fw_kind && i.description).map(i => [String(i.description).trim().toLowerCase().replace(/\s+/g, ' '), i]));
    const rows = groups.map(g => {
      const line = manual.find(i => i.fw_kind === g.key) || byDesc.get(g.label.toLowerCase().replace(/\s+/g, ' ')) || null;
      return { ...g, line, change: !line ? 'new' : Number(line.quantity) === g.qty ? 'same' : 'qty' };
    });
    const gone = fwLines.filter(i => !keys.has(i.fw_kind) && Number(i.quantity) !== 0)
      .map(i => ({ key: i.fw_kind, label: i.description || i.fw_kind, count: 0, qty: 0, unit: i.unit, ids: [], line: i, change: 'gone' }));
    return [...rows, ...gone];
  }, [groups, items]); // eslint-disable-line react-hooks/exhaustive-deps

  const picked = (plan || []).filter(r => !off.has(r.key) && r.change !== 'same');
  const newLine = (r, n) => ({ ...makeItem(manual.length + n), description: r.label, quantity: r.qty, unit: r.unit, fw_kind: r.key, notes: lineNotes(r) });
  function apply() {
    const byKey = new Map(picked.map(r => [r.key, r]));
    let next = items.map(i => {
      const r = i.fw_kind ? byKey.get(i.fw_kind) : [...byKey.values()].find(x => x.line && x.line._key === i._key);
      if (!r || !r.line || r.line._key !== i._key) return i;
      if (r.change === 'gone') return { ...i, quantity: 0, flag: 'red' };
      return { ...i, quantity: r.qty, fw_kind: r.key };   // only the quantity changes — the rest is yours
    });
    const added = picked.filter(r => r.change === 'new').map(newLine);
    next = [...next, ...added];
    onApply(next, { added: added.length, updated: picked.filter(r => r.change === 'qty').length, zeroed: picked.filter(r => r.change === 'gone').length });
  }
  // ↺ Start over: every line this made comes out; the ticked groups go back in fresh.
  function startOver() {
    const priced = fwLines.filter(i => Number(i.material_cost) || Number(i.labor_hours) || Number(i.dje_standard) || Number(i.dje_takeoff) || (Array.isArray(i.sub_items) && i.sub_items.length)).length;
    if (!window.confirm(`Take out the ${fwLines.length} line${fwLines.length === 1 ? '' : 's'} made from Fieldwire${priced ? ` (${priced} with prices typed on them — those prices go too)` : ''} and put in a fresh read of the tasks?`)) return;
    const fresh = (groups || []).filter(g => !off.has(g.key)).map(newLine);
    onApply([...items.filter(i => !i.fw_kind), ...fresh], { added: fresh.length, updated: 0, zeroed: 0, removed: fwLines.length });
  }

  const tag = r => r.change === 'new' ? { t: 'new line', c: '#15803d', bg: '#f0fdf4' }
    : r.change === 'qty' ? { t: `qty ${Number(r.line.quantity)} → ${r.qty}`, c: '#1d4ed8', bg: '#eff6ff' }
    : r.change === 'gone' ? { t: 'no tasks left → qty 0, flagged red', c: '#b91c1c', bg: '#fef2f2' }
    : { t: 'up to date', c: '#9ca3af', bg: '#f9fafb' };
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 6000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: 'min(760px, 96vw)', maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 12px 48px rgba(0,0,0,0.3)' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #eef0f2' }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: '#111827' }}>📍 Lines from Fieldwire</div>
          <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 3 }}>One line for each Fieldwire category (tasks with no category: one line per clipped table) — how many tasks, or the feet when the names or the table give lengths. Run it again any time to sync: only the quantities change; prices and everything you typed stay.</div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '10px 20px' }}>
          {err && <div style={{ color: '#b91c1c', fontWeight: 700, fontSize: 13 }}>{err}</div>}
          {oldStyle > 0 && <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 10px', fontSize: 12.5, color: '#92400e', margin: '6px 0' }}>{oldStyle} line{oldStyle === 1 ? ' was' : 's were'} made the first way (one per task name). Press <b>↺ Start over</b> to replace them with one line per category.</div>}
          {!plan && !err && <div style={{ color: '#9ca3af', fontSize: 13 }}>Reading the task list…</div>}
          {plan && !plan.length && <div style={{ color: '#9ca3af', fontSize: 13 }}>No tasks on this bid's Fieldwire project yet.</div>}
          {plan && plan.map(r => {
            const g = tag(r); const same = r.change === 'same';
            return (
              <label key={r.key + r.change} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 4px', borderBottom: '1px solid #f3f4f6', cursor: same ? 'default' : 'pointer', opacity: same ? 0.6 : 1 }}>
                <input type="checkbox" disabled={same} checked={!same && !off.has(r.key)} onChange={() => setOff(s => { const n = new Set(s); n.has(r.key) ? n.delete(r.key) : n.add(r.key); return n; })} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: '#111827' }}>{r.label}</div>
                  <div style={{ fontSize: 11.5, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.count} task{r.count === 1 ? '' : 's'}{r.unit === 'LF' ? ' · ' + feetWord(r) + (r.noFeet ? ` (${r.noFeet} with no length)` : '') : ''}{r.ids.length ? ' · ' + r.ids.slice(0, 12).join(', ') + (r.ids.length > 12 ? ` +${r.ids.length - 12}` : '') : ''}
                  </div>
                </div>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#111827', minWidth: 70, textAlign: 'right' }}>{r.qty}<span style={{ fontSize: 11, color: '#6b7280', fontWeight: 700 }}> {r.unit === 'LF' ? 'LF' : 'ea'}</span></div>
                <span style={{ fontSize: 11, fontWeight: 800, color: g.c, background: g.bg, borderRadius: 999, padding: '3px 9px', whiteSpace: 'nowrap' }}>{g.t}</span>
              </label>
            );
          })}
        </div>
        <div style={{ padding: '12px 20px', borderTop: '1px solid #eef0f2', display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 12.5, color: '#6b7280', flex: 1 }}>
            {picked.length ? `${picked.filter(r => r.change === 'new').length} new · ${picked.filter(r => r.change === 'qty').length} updated · ${picked.filter(r => r.change === 'gone').length} to zero` : plan && plan.length ? 'Everything is up to date.' : ''}
          </span>
          {fwLines.length > 0 && <button onClick={startOver} disabled={!groups} title="Take out every line made from Fieldwire and read the tasks fresh" style={{ ...btn, color: '#b45309', borderColor: '#fcd34d' }}>↺ Start over</button>}
          <button onClick={onClose} style={btn}>Cancel</button>
          <button onClick={apply} disabled={!picked.length} style={{ ...btn, border: 'none', background: picked.length ? '#e87722' : '#fed7aa', color: '#fff', cursor: picked.length ? 'pointer' : 'default' }}>
            {plan && plan.some(r => r.line) ? 'Sync the lines' : 'Add the lines'}
          </button>
        </div>
      </div>
    </div>
  );
}
